import {
    getLessons as apiGetLessons,
    postClessonFinish,
    patchMarkCLessonsAsRead,
} from '@yandex-int/k-common/client/api';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { LESSONS_PAGE_SIZE } from '@yandex-int/k-common/constants';
import { CLessonInCourse, FullCLesson, LESSONS_TYPES } from '@yandex-int/k-common/typings';
import get from 'lodash/get';
import { NOTIFICATION_THEMES, GRAVITIES } from 'platform-components/constants';
import { showNetworkError, showIntlMessageNotification } from 'platform-components/src/components/notification/actions';
import { call, put, race, take, takeEvery, takeLatest, all, select, fork } from 'redux-saga/effects';

import * as closingLessonModalActions from 'common.components/closing-lesson-modal/actions';
import { CANCEL_LESSON_CLOSING, CONFIRM_LESSON_CLOSING } from 'common.components/closing-lesson-modal/constants';
import { COMMON_NOTIFICATION_DELAY } from 'common.components/constants';
import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { actions as lessonActions } from 'common.components/lesson';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import { emitSocketForceCompleteLesson } from 'common.components/lesson/sagas';
import { getLesson, getLessonLoadingStatus } from 'common.components/lesson/selectors';
import { forceCompleteLesson } from 'common.components/lessons/actions';
import { FORCE_COMPLETE_LESSON } from 'common.components/lessons/constants';
import { getIsInOwnClass } from 'common.components/user/selectors';
import { actions as coursesActions } from 'store/courses/actions';
import { likesActions } from 'store/likes/actions';
import * as likesSelectors from 'store/likes/selectors';
import { getTeamIdFromPath } from 'utils/path';

import * as actions from './actions';
import * as constants from './constants';
import {
    getCompletedLessonsSortKey,
    getDraftsSortKey,
    getActiveLessonsSortKey,
    getAssignedLessonsSortKey,
} from './selectors';

export function* courseViewSagaWatcher() {
    yield all([
        takeEvery(constants.GET_DRAFTS_REQUEST, getDraftsSaga),
        takeEvery(constants.GET_COMPLETED_LESSONS_REQUEST, getCompletedLessonsSaga),
        takeLatest(constants.GET_ACTIVE_LESSONS_REQUEST, getActiveLessonsSaga),
        takeLatest(constants.GET_ASSIGNED_LESSONS_REQUEST, getAssignedLessonsSaga),
        takeLatest(FORCE_COMPLETE_LESSON, forceCompleteLessonSaga),
        takeLatest(lessonActions.lessonUpdated, clearLessonsSaga),
        // для списка занятий outside click обрабатывается аналогично закрытию по крестику
        takeLatest(constants.CLOSE_LESSON_MODAL, closeModalSaga),
        takeLatest(constants.MARK_CLESSON_AS_READ, markCLessonAsReadSaga),
        takeLatest(constants.GET_CLESSON_REACTIONS, getClessonReactionsRequestSaga),
    ]);
}

function* getDraftsSaga(action: ReturnType<typeof actions.getDraftsRequest>) {
    const { courseId } = action;
    try {
        const sortKey: number | null = yield select(getDraftsSortKey, { courseId });

        const { clessons, sortKey: newSortKey }: { sortKey: number; clessons: Array<CLessonInCourse> } = yield call(
            apiGetLessons,
            courseId,
            {
                type: LESSONS_TYPES.UNASSIGNED,
                pageSize: LESSONS_PAGE_SIZE,
                sortKey,
            }
        );

        yield put(actions.getDraftsSuccess(courseId, sortKey, newSortKey, clessons));
    } catch (error) {
        console.error(error);

        yield put(actions.getDraftsError(courseId));
        yield put(showNetworkError(error));
    }
}

function* getCompletedLessonsSaga(action: ReturnType<typeof actions.getCompletedLessonsRequest>) {
    try {
        const { courseId, onSuccess } = action;
        const sortKey: number = yield select(getCompletedLessonsSortKey, { courseId });
        const lessonsRequestData = {
            type: LESSONS_TYPES.FINISHED,
            pageSize: LESSONS_PAGE_SIZE,
            sortKey,
        };

        const { clessons, sortKey: newSortKey }: { sortKey: number; clessons: Array<CLessonInCourse> } = yield call(
            apiGetLessons,
            courseId,
            lessonsRequestData
        );

        yield put(actions.getCompletedLessonsSuccess(courseId, sortKey, newSortKey, clessons));

        if (onSuccess) {
            onSuccess(clessons);
        }
    } catch (error) {
        console.error(error);

        yield put(actions.getCompletedLessonsError(action.courseId));
        yield put(showNetworkError(error));
    }
}

function* getActiveLessonsSaga(action: ReturnType<typeof actions.getActiveLessonsRequest>) {
    try {
        const { courseId, onSuccess } = action;
        const prevSortKey: number | null = yield select(getActiveLessonsSortKey, { courseId });
        const lessonsRequestData = {
            type: LESSONS_TYPES.ACTIVE,
            pageSize: LESSONS_PAGE_SIZE,
            sortKey: prevSortKey,
        };

        const { clessons, sortKey }: { sortKey: number; clessons: Array<CLessonInCourse> } = yield call(
            apiGetLessons,
            courseId,
            lessonsRequestData
        );

        yield put(actions.getActiveLessonsSuccess(courseId, clessons, sortKey));
        if (onSuccess) {
            onSuccess(clessons);
        }
    } catch (error) {
        console.error(error);

        yield put(actions.getActiveLessonsError(action.courseId));
        yield put(showNetworkError(error));
    }
}

function* getAssignedLessonsSaga(action: ReturnType<typeof actions.getAssignedLessonsRequest>) {
    try {
        const { courseId, onSuccess } = action;
        const prevSortKey: number = yield select(getAssignedLessonsSortKey, { courseId });
        const lessonsRequestData = {
            type: LESSONS_TYPES.ASSIGNED,
            pageSize: LESSONS_PAGE_SIZE,
            sortKey: prevSortKey,
        };

        const { clessons, sortKey }: { sortKey: number; clessons: Array<CLessonInCourse> } = yield call(
            apiGetLessons,
            courseId,
            lessonsRequestData
        );

        yield put(actions.getAssignedLessonsSuccess(courseId, clessons, sortKey));
        if (onSuccess) {
            onSuccess(clessons);
        }
    } catch (error) {
        console.error(error);

        yield put(actions.getAssignedLessonsError(action.courseId));
        yield put(showNetworkError(error));
    }
}

function* forceCompleteLessonSaga(action: ReturnType<typeof forceCompleteLesson>) {
    try {
        const {
            clesson: {
                id: clessonId,
                lesson: { name: lessonName },
            },
            courseId,
            io,
            websocketDomain,
            onSuccess,
        } = action;

        yield put(closingLessonModalActions.toggleModal(true));
        const { confirm } = yield race({
            confirm: take(CONFIRM_LESSON_CLOSING),
            cancel: take(CANCEL_LESSON_CLOSING),
        });

        if (confirm) {
            yield call(postClessonFinish, clessonId);
            yield fork(emitSocketForceCompleteLesson, courseId, clessonId, io, websocketDomain);
            yield put(actions.clearLessons());
            yield put(closingLessonModalActions.toggleModal(false));
            yield put(
                showIntlMessageNotification({
                    closeDelay: COMMON_NOTIFICATION_DELAY,
                    gravity: GRAVITIES.BOTTOM,
                    messageId: 'toast.courseView.notification.lessonFinishForced',
                    messageValues: { lessonName },
                    withExplicitClose: true,
                    theme: NOTIFICATION_THEMES.TOAST,
                })
            );

            if (onSuccess) {
                onSuccess();
            }
        } else {
            yield put(closingLessonModalActions.toggleModal(false));
        }
    } catch (error) {
        console.error(error);
        yield put(showNetworkError(error));
    }
}

function* clearLessonsSaga() {
    const courseId: number = yield select(getCurrentYearCourseId);

    yield put(actions.clearLessons());
    yield put(coursesActions.clearCourseLessonCount({ courseId }));
}

function* closeModalSaga() {
    const currentTeamId = getTeamIdFromPath();

    const lesson: FullCLesson | null = yield select(getLesson);
    const loadingStatus: LOADING_STATUSES = yield select(getLessonLoadingStatus);
    const isInOwnClass: boolean = yield select(getIsInOwnClass, { classId: currentTeamId });
    const clessonId: number | undefined = get(lesson, 'clesson.id');
    const courseId: number | undefined = get(lesson, ['clesson', 'course', 'id']);

    yield put(lessonViewActions.toggleModal(false));
    yield put(lessonActions.clearEditor());

    const canMarkCLessonAsRead =
        courseId &&
        clessonId &&
        // Если КМ зайдут в чужой курс, то помечать прочитанным занятие не нужно
        isInOwnClass &&
        // Если занятие не загружено, то мы не можем выставить ему статус просмотрено
        // что и логично - ничего не успели увидеть
        loadingStatus === LOADING_STATUSES.SUCCESS;

    if (canMarkCLessonAsRead) {
        yield put(actions.markCLessonAsRead(courseId, clessonId));
    }
}

function* markCLessonAsReadSaga(action: ReturnType<typeof actions.markCLessonAsRead>) {
    try {
        const { clessonId } = action;

        yield call(patchMarkCLessonsAsRead, [clessonId!]);
    } catch (error) {
        console.error(error);

        yield put(showNetworkError(error));
    }
}

function* getClessonReactionsRequestSaga(action: ReturnType<typeof actions.getClessonReactionsRequest>) {
    try {
        const { classId, subject, clessons } = action;
        const clessonIds = clessons.map(({ id }) => id);
        if (!(classId && subject && clessonIds.length > 0)) {
            return;
        }

        const shouldShowLikes: boolean = yield select(likesSelectors.getShouldShowLikeReactions, { classId, subject });

        if (shouldShowLikes) {
            yield put(likesActions.getClessonReactions({ clessonIds }));
        }
    } catch (error) {
        console.error(error);

        yield put(showNetworkError(error));
    }
}
